import { Routes, Route, Navigate } from 'react-router-dom'
import { Layout } from './components/layout/Layout'
import { Dashboard } from './pages/Dashboard'
import { usePermissions } from './hooks/usePermissions'
import { Employees } from './pages/Employees'
import { Departments } from './pages/Departments'
import { Positions } from './pages/Positions'
import { Organizations } from './pages/Organizations'
import { Schedules } from './pages/Schedules'
import { StaffingLimits } from './pages/StaffingLimits'
import { Attendance } from './pages/Attendance'
import { TimeRecords } from './pages/TimeRecords'
import { Reports } from './pages/Reports'
import { LocationsPage } from './components/locations/LocationsPage'
import { Notifications } from './pages/Notifications'
import { Testing } from './pages/Testing'
import { MyTests } from './pages/MyTests'
import { Documents } from './pages/Documents'
import { News } from './pages/News'
import { Feedback } from './pages/Feedback'
import { Gamification } from './pages/Gamification'
import { ScheduleManagement } from './pages/ScheduleManagement'
import { ScheduleChangeRequests } from './pages/ScheduleChangeRequests'
import { SmartScheduleSettings } from './pages/SmartScheduleSettings'
import { ShiftReplacements } from './pages/ShiftReplacements'
import { ActivityLogs } from './pages/ActivityLogs'
import { Profile } from './pages/Profile'
import { Settings } from './pages/Settings'
import { Roles } from './pages/Roles'
import { Users } from './pages/Users'
import { Login } from './pages/Login'
import { Register } from './pages/Register'
import { NotFound } from './pages/NotFound'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import './App.css'

const permissionRoutes = [
  { permission: 'employees', path: '/' },
  { permission: 'my_tests', path: '/my-tests' },
  { permission: 'testing', path: '/testing' },
  { permission: 'documents', path: '/documents' },
  { permission: 'feedback', path: '/feedback' },
  { permission: 'gamification', path: '/gamification' },
  { permission: 'schedule_requests', path: '/schedule-management' },
  { permission: 'schedule_requests', path: '/schedule-change-requests' },
  { permission: 'attendance', path: '/attendance' },
  { permission: 'time_records', path: '/time-records' },
  { permission: 'notifications', path: '/notifications' },
] as const;

function DefaultRedirect() {
  const { hasPermission, isAdmin } = usePermissions();
  if (isAdmin() || hasPermission('employees', 'read')) {
    return <Dashboard />;
  }
  const firstAvailable = permissionRoutes.find(r => r.permission !== 'employees' && hasPermission(r.permission, 'read'));
  return <Navigate to={firstAvailable?.path ?? '/profile'} replace />;
}

function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />

      {/* Protected routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<DefaultRedirect />} />
        <Route path="employees" element={<ProtectedRoute permission="employees"><Employees /></ProtectedRoute>} />
        <Route path="departments" element={<ProtectedRoute permission="departments"><Departments /></ProtectedRoute>} />
        <Route path="positions" element={<ProtectedRoute permission="positions"><Positions /></ProtectedRoute>} />
        <Route path="organizations" element={<ProtectedRoute permission="organizations"><Organizations /></ProtectedRoute>} />
        <Route path="locations" element={<ProtectedRoute permission="locations"><LocationsPage /></ProtectedRoute>} />
        <Route path="schedules" element={<ProtectedRoute permission="schedules"><Schedules /></ProtectedRoute>} />
        <Route path="staffing-limits" element={<ProtectedRoute permission="staffing_limits"><StaffingLimits /></ProtectedRoute>} />
        <Route path="attendance" element={<ProtectedRoute permission="attendance"><Attendance /></ProtectedRoute>} />
        <Route path="time-records" element={<ProtectedRoute permission="time_records"><TimeRecords /></ProtectedRoute>} />
        <Route path="reports" element={<ProtectedRoute permission="reports"><Reports /></ProtectedRoute>} />
        <Route path="notifications" element={<ProtectedRoute permission="notifications"><Notifications /></ProtectedRoute>} />
        <Route path="testing" element={<ProtectedRoute permission="testing"><Testing /></ProtectedRoute>} />
        {/* Чек-листы временно скрыты до запуска модуля: вернуть импорт Checklists и маршрут
            <Route path="checklists" element={<ProtectedRoute permission="checklists"><Checklists /></ProtectedRoute>} /> */}
        <Route path="documents" element={<ProtectedRoute permission="documents"><Documents /></ProtectedRoute>} />
        <Route path="news" element={<ProtectedRoute permission="news"><News /></ProtectedRoute>} />
        <Route path="feedback" element={<ProtectedRoute permission="feedback"><Feedback /></ProtectedRoute>} />
        <Route path="gamification" element={<ProtectedRoute permission="gamification"><Gamification /></ProtectedRoute>} />
        <Route path="activity-logs" element={<ProtectedRoute permission="activity_logs"><ActivityLogs /></ProtectedRoute>} />
        <Route path="users" element={<ProtectedRoute permission="users"><Users /></ProtectedRoute>} />
        <Route path="roles" element={<ProtectedRoute adminOnly><Roles /></ProtectedRoute>} />
        <Route path="settings" element={<ProtectedRoute permission="settings"><Settings /></ProtectedRoute>} />
        <Route path="my-tests" element={<ProtectedRoute permission="my_tests"><MyTests /></ProtectedRoute>} />
        <Route path="schedule-management" element={<ProtectedRoute permission="schedule_requests"><ScheduleManagement /></ProtectedRoute>} />
        <Route path="schedule-change-requests" element={<ProtectedRoute permission="schedule_requests"><ScheduleChangeRequests /></ProtectedRoute>} />
        <Route path="smart-schedule" element={<ProtectedRoute permission="smart_schedule"><SmartScheduleSettings /></ProtectedRoute>} />
        <Route path="shift-replacements" element={<ProtectedRoute permission="smart_schedule"><ShiftReplacements /></ProtectedRoute>} />
        <Route path="profile" element={<Profile />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

export default App
